import React from 'react';

interface EmptyStateProps {
  /**
   * Optional heading (h3) above the message. Use for top-level empty
   * states; omit for inline empty states within cards.
   */
  heading?: string;
  /**
   * Required descriptive copy explaining what the user is missing
   * and (where applicable) what to do about it.
   */
  message: React.ReactNode;
  /**
   * Optional CTA rendered as a button-styled link below the message.
   */
  ctaText?: string;
  ctaHref?: string;
  /**
   * Wrap the empty state in a `card` container. Default true.
   * Set false for inline empty states within an existing card.
   */
  card?: boolean;
}

/**
 * Canonical empty-state component for Portal routes. Replaces the
 * ad-hoc `<div className="card"><p className="muted">No items…</p></div>`
 * pattern.
 */
export const EmptyState = ({
  heading,
  message,
  ctaText,
  ctaHref,
  card = true
}: EmptyStateProps): JSX.Element => {
  const content = (
    <>
      {heading && <h3>{heading}</h3>}
      <p className="muted">{message}</p>
      {ctaText && ctaHref && (
        <a className="cta" href={ctaHref}>
          {ctaText}
        </a>
      )}
    </>
  );
  if (!card) {
    return <div>{content}</div>;
  }
  return <div className="card">{content}</div>;
};
